/**
 * Owner association resolution.
 *
 * Resolves the kind-aware owner association name for an entity type — i.e.
 * the `*_to_owner` association pointing at `ahoy_user` with `many_to_one`
 * cardinality. Kind-awareness is mandatory: the board query's
 * `include_associations` key is validated server-side against
 * `AssociationType.matches_source`, and a naive type-name-only lookup can
 * pick a name that fails that check, 400ing the board query.
 *
 * KD2: when both a kind-based and a type-based candidate match, the
 * kind-based candidate wins (kind-based associations are the canonical,
 * forward-looking binding).
 *
 * KD3: when no association matches the source predicate at all (e.g. a
 * cold schema cache mid-rollout), fall back to the conventional
 * `${entityType}_to_owner` name if it's present in the raw list, else ''.
 *
 * `*_timeline` identifiers are reserved for internal use and are never valid
 * board entity types (see `isValidEntityTypeName` in `core/types.ts`, which
 * mirrors the backend's rejection). Reject them up front so the fallback can
 * never synthesize a `<timeline>_to_owner` key that would 400 the board query.
 */

import type { AssociationType } from '../core/SchemaClient';
import { associationMatchesSource } from './associationMatching';

const OWNER_SUFFIX = '_to_owner';
const USER_ENTITY_TYPE = 'ahoy_user';
const TIMELINE_SUFFIX = '_timeline';

export function resolveOwnerAssociationName(
  associationTypes: AssociationType[],
  entityType: string,
  entityKinds: readonly string[]
): string {
  if (entityType.endsWith(TIMELINE_SUFFIX)) {
    return '';
  }

  const candidates = associationTypes.filter(
    at =>
      associationMatchesSource(at, entityType, entityKinds) &&
      at.association_type_name.endsWith(OWNER_SUFFIX) &&
      at.target_entity_type_name === USER_ENTITY_TYPE &&
      at.cardinality === 'many_to_one'
  );

  if (candidates.length > 0) {
    const kindBased = candidates.filter(at =>
      Boolean(at.source_entity_kind_name)
    );
    const chosen = kindBased.length > 0 ? kindBased : candidates;
    return chosen.map(at => at.association_type_name).sort()[0];
  }

  const fallbackName = `${entityType}${OWNER_SUFFIX}`;
  if (associationTypes.some(at => at.association_type_name === fallbackName)) {
    return fallbackName;
  }
  return '';
}
